import React from "react";
import PropTypes from "prop-types";
import { sanitizeHtml } from "./sanitize";
import ChannelChip from "../../molecules/ChannelChip";
import UserChip from "../../molecules/UserChip";
import ChatImage from "../ChatImage";
import LockIcon from "../../atoms/icons/LockIcon";
import { Link } from "../../atoms/Link";
import { splitTextIntoLinkParts } from "../../../utils/linkDetection";

// Render sanitized HTML and convert @user and #channel tokens inside text nodes into chips.
// When content is inside <code> or <pre>, render it as-is (no mentions/linking), Slack-like.
export default function RichTextRenderer({ html, channels = [], friends = [], groupId, onOpenChannel, onOpenUser }) {
  const channelMap = React.useMemo(() => {
    const m = new Map();
    (channels || []).forEach((ch) => { if (ch?.slug) m.set(String(ch.slug).toLowerCase(), ch); });
    return m;
  }, [channels]);
  const userMap = React.useMemo(() => {
    const m = new Map();
    (friends || []).forEach((u) => { const n = (u?.username || '').toLowerCase(); if (n) m.set(n, u); });
    return m;
  }, [friends]);

  const safe = sanitizeHtml(html || "");

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${safe}</div>`, 'text/html');
  const root = doc.body.firstChild;

  const renderMentionsAndChannels = React.useCallback((text, keyPrefix) => {
    const parts = [];
    const regex = /(^|[^A-Za-z0-9_])([@#])([A-Za-z0-9_.-]{2,80})\b/g;
    let last = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const [full, prefix, marker, token] = match;
      const start = match.index;
      if (start > last) parts.push(text.slice(last, start));
      if (prefix) parts.push(prefix);
      if (marker === '@') {
        const user = userMap.get(token.toLowerCase());
        if (user) {
          parts.push(<UserChip key={`${keyPrefix}-u-${start}`} user={user} onClick={() => onOpenUser?.(user)} />);
        } else {
          const fallbackUser = { username: token };
          parts.push(<UserChip key={`${keyPrefix}-u-${start}`} user={fallbackUser} onClick={() => onOpenUser?.(fallbackUser)} />);
        }
      } else {
        const channel = channelMap.get(token.toLowerCase());
        if (channel) {
          parts.push(<ChannelChip key={`${keyPrefix}-c-${start}`} groupId={groupId} channel={channel} onClick={() => onOpenChannel?.(channel)} />);
        } else {
          parts.push(
            <span key={`${keyPrefix}-pc-${start}`} className="mention--private-channel" title="Private channel">
              <LockIcon width={12} height={12} />
              <span style={{ marginLeft: 4 }}>private-channel</span>
            </span>
          );
        }
      }
      last = start + full.length;
    }

    if (last < text.length) parts.push(text.slice(last));
    return parts.length ? parts : text;
  }, [channelMap, groupId, onOpenChannel, onOpenUser, userMap]);

  const renderNode = (node, key, inCode = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue || "";
      if (inCode) {
        // In code mode, render text literally without mention/channel parsing
        return text;
      }
      const renderedParts = [];

      splitTextIntoLinkParts(text).forEach((part, index) => {
        if (part.type !== 'url') {
          renderedParts.push(...[].concat(renderMentionsAndChannels(part.value, `${key}-text-${index}`)));
          return;
        }

        if (!part.url) {
          renderedParts.push(part.original);
          return;
        }

        renderedParts.push(
          <Link
            key={`${key}-link-${index}`}
            to={part.url}
            external
          >
            {part.url}
          </Link>
        );

        if (part.trailingText) {
          renderedParts.push(part.trailingText);
        }
      });

      return renderedParts.length ? renderedParts : text;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const tag = node.tagName.toLowerCase();
    const nextInCode = inCode || tag === 'code' || tag === 'pre';
    const children = Array.from(node.childNodes).map((child, i) => renderNode(child, `${key}-${i}`, nextInCode));
    const props = {};
    if (!nextInCode && tag === 'a') {
      const href = node.getAttribute('href') || '#';
      props.to = href;
      props.external = true;
      const title = node.getAttribute('title');
      if (title) {
        props.title = title;
      }
      return React.createElement(Link, { ...props, key }, children);
    }
    // Normalize tags to safe React elements (keep in-sync with sanitizer)
    const allowed = ['b','strong','i','em','s','strike','u','code','pre','blockquote','ul','ol','li','br','p','div','span','img'];
    const voidTags = new Set(['br', 'hr', 'img', 'input', 'meta', 'link']);
    const safeTag = allowed.includes(tag) ? tag : 'span';
    if (safeTag === 'img') {
      const src = node.getAttribute('src');
      if (!src) {
        return null;
      }
      const alt = node.getAttribute('alt') || "";
      const loading = node.getAttribute('loading') || undefined;
      const title = node.getAttribute('title') || undefined;
      const width = node.getAttribute('width') || undefined;
      const height = node.getAttribute('height') || undefined;
      const imageProps = {};
      if (title) {
        imageProps.title = title;
      }
      if (width) {
        imageProps.width = width;
      }
      if (height) {
        imageProps.height = height;
      }
      return (
        <ChatImage
          key={key}
          src={src}
          alt={alt}
          loading={loading}
          variant="inline"
          imageProps={imageProps}
        />
      );
    }
    if (voidTags.has(safeTag)) {
      // Void elements must not have children in React
      return React.createElement(safeTag, { key });
    }
    return React.createElement(safeTag, { key }, children);
  };

  const nodes = Array.from(root.childNodes).map((child, i) => renderNode(child, `n${i}`));
  return <>{nodes}</>;
}

RichTextRenderer.propTypes = {
  html: PropTypes.string,
  channels: PropTypes.array,
  friends: PropTypes.array,
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onOpenChannel: PropTypes.func,
  onOpenUser: PropTypes.func,
};
